import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface Account {
  /** DID for live accounts. */
  id: string
  handle: string
  did: string
  service: string
  displayName: string | null
  avatar: string | null
  accessJwt: string
  refreshJwt: string
  addedAt: number
  /** Set when the PDS rejects this account (deactivated /
   *  suspended / takedown / unknown actor). Live polls bail out;
   *  cached IndexedDB views still render. */
  deactivated?: boolean
  /** Short string from the PDS error code (e.g.
   *  "AccountDeactivated"). Surfaced in the deactivated banner. */
  deactivationReason?: string
  /** ms epoch of when we last marked this account dead. */
  deactivatedAt?: number
}

/**
 * Discord-style folder. The "default" group always exists with id `default`
 * — accounts not explicitly placed in another group live there. Groups are
 * renderable, draggable, and collapsible in the rail.
 */
export interface AccountGroup {
  id: string
  name: string
  /** Tailwind-friendly accent (hsl). Falls back when no icon is set. */
  color: string
  /** Optional uploaded icon — stored as a data URL (small images
   *  only). When present, the rail renders this in place of the
   *  color swatch. */
  icon?: string
  accountIds: string[]
  collapsed?: boolean
}

const DEFAULT_GROUP_ID = 'default'

const DEFAULT_GROUP: AccountGroup = {
  id: DEFAULT_GROUP_ID,
  name: 'accounts',
  color: 'hsl(199 89% 48%)',
  accountIds: [],
  collapsed: false,
}

interface AccountsState {
  accounts: Account[]
  groups: AccountGroup[]
  activeId: string | null
  add: (a: Account) => void
  remove: (id: string) => void
  setActive: (id: string | null) => void
  reorder: (ids: string[]) => void
  updateTokens: (id: string, accessJwt: string, refreshJwt: string) => void
  markDeactivated: (id: string, reason: string) => void
  markReactivated: (id: string) => void
  updateProfile: (
    id: string,
    patch: Partial<Pick<Account, 'displayName' | 'avatar' | 'handle'>>,
  ) => void
  // Group ops
  createGroup: (name?: string, color?: string) => string
  renameGroup: (id: string, name: string) => void
  recolorGroup: (id: string, color: string) => void
  setGroupIcon: (id: string, icon: string | undefined) => void
  deleteGroup: (id: string) => void
  toggleGroup: (id: string) => void
  reorderGroups: (ids: string[]) => void
  moveAccountToGroup: (accountId: string, toGroupId: string, toIndex?: number) => void
  reset: () => void
}

const PALETTE = [
  'hsl(199 89% 48%)',
  'hsl(280 70% 60%)',
  'hsl(150 70% 50%)',
  'hsl(30 92% 58%)',
  'hsl(340 82% 58%)',
  'hsl(180 70% 50%)',
]

function ensureGroup(state: { groups: AccountGroup[] }) {
  if (!state.groups.some((g) => g.id === DEFAULT_GROUP_ID)) {
    state.groups = [DEFAULT_GROUP, ...state.groups]
  }
}

export const useAccounts = create<AccountsState>()(
  persist(
    (set, get) => ({
      accounts: [],
      groups: [DEFAULT_GROUP],
      activeId: null,
      add: (a) =>
        set((s) => {
          const exists = s.accounts.some((x) => x.id === a.id)
          const accounts = exists
            ? s.accounts.map((x) => (x.id === a.id ? { ...x, ...a } : x))
            : [...s.accounts, a]
          // Place new accounts in the default group if not already grouped.
          const groups = s.groups.map((g) => ({ ...g, accountIds: [...g.accountIds] }))
          ensureGroup({ groups })
          const placed = groups.some((g) => g.accountIds.includes(a.id))
          if (!placed) {
            const def = groups.find((g) => g.id === DEFAULT_GROUP_ID)!
            def.accountIds.push(a.id)
          }
          return { accounts, groups, activeId: s.activeId ?? a.id }
        }),
      remove: (id) =>
        set((s) => {
          const accounts = s.accounts.filter((x) => x.id !== id)
          const groups = s.groups.map((g) => ({
            ...g,
            accountIds: g.accountIds.filter((x) => x !== id),
          }))
          const activeId =
            s.activeId === id ? (accounts[0]?.id ?? null) : s.activeId
          return { accounts, groups, activeId }
        }),
      setActive: (id) => set({ activeId: id }),
      reorder: (ids) =>
        set((s) => ({
          accounts: ids
            .map((i) => s.accounts.find((a) => a.id === i))
            .filter((a): a is Account => !!a),
        })),
      updateTokens: (id, accessJwt, refreshJwt) =>
        set((s) => ({
          accounts: s.accounts.map((a) =>
            a.id === id
              ? {
                  ...a,
                  accessJwt,
                  refreshJwt,
                  // Successful refresh implicitly clears the
                  // deactivated flag — the PDS just gave us new
                  // tokens, so it's alive again.
                  deactivated: false,
                  deactivationReason: undefined,
                  deactivatedAt: undefined,
                }
              : a,
          ),
        })),
      markDeactivated: (id, reason) => {
        set((s) => ({
          accounts: s.accounts.map((a) =>
            a.id === id
              ? {
                  ...a,
                  deactivated: true,
                  deactivationReason: reason,
                  deactivatedAt: Date.now(),
                }
              : a,
          ),
        }))
        // Permanently deactivated accounts auto-remove after a short delay.
        // SessionExpired / InvalidToken are transient (user can re-login) and
        // are NOT removed here.
        if (/AccountDeactivated|AccountTakedown|AccountSuspended/i.test(reason)) {
          setTimeout(() => get().remove(id), 5_000)
        }
      },
      markReactivated: (id) =>
        set((s) => ({
          accounts: s.accounts.map((a) =>
            a.id === id
              ? {
                  ...a,
                  deactivated: false,
                  deactivationReason: undefined,
                  deactivatedAt: undefined,
                }
              : a,
          ),
        })),
      updateProfile: (id, patch) =>
        set((s) => ({
          accounts: s.accounts.map((a) => (a.id === id ? { ...a, ...patch } : a)),
        })),
      createGroup: (name, color) => {
        const id = `g_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`
        const palette = color ?? PALETTE[get().groups.length % PALETTE.length]
        set((s) => ({
          groups: [
            ...s.groups,
            { id, name: name ?? 'group', color: palette, accountIds: [], collapsed: false },
          ],
        }))
        return id
      },
      renameGroup: (id, name) =>
        set((s) => ({
          groups: s.groups.map((g) => (g.id === id ? { ...g, name } : g)),
        })),
      recolorGroup: (id, color) =>
        set((s) => ({
          groups: s.groups.map((g) => (g.id === id ? { ...g, color } : g)),
        })),
      setGroupIcon: (id, icon) =>
        set((s) => ({
          groups: s.groups.map((g) =>
            g.id === id ? { ...g, icon: icon ?? undefined } : g,
          ),
        })),
      deleteGroup: (id) =>
        set((s) => {
          if (id === DEFAULT_GROUP_ID) return s
          // Move any accounts in this group back to default.
          const target = s.groups.find((g) => g.id === id)
          if (!target) return s
          const def = s.groups.find((g) => g.id === DEFAULT_GROUP_ID)
          const groups = s.groups
            .filter((g) => g.id !== id)
            .map((g) =>
              g.id === DEFAULT_GROUP_ID && def
                ? { ...g, accountIds: [...g.accountIds, ...target.accountIds] }
                : g,
            )
          return { groups }
        }),
      toggleGroup: (id) =>
        set((s) => ({
          groups: s.groups.map((g) =>
            g.id === id ? { ...g, collapsed: !g.collapsed } : g,
          ),
        })),
      reorderGroups: (ids) =>
        set((s) => ({
          groups: ids
            .map((i) => s.groups.find((g) => g.id === i))
            .filter((g): g is AccountGroup => !!g),
        })),
      moveAccountToGroup: (accountId, toGroupId, toIndex) =>
        set((s) => {
          const groups = s.groups.map((g) => ({
            ...g,
            accountIds: g.accountIds.filter((x) => x !== accountId),
          }))
          const target = groups.find((g) => g.id === toGroupId)
          if (!target) return s
          const idx = toIndex ?? target.accountIds.length
          target.accountIds.splice(idx, 0, accountId)
          return { groups }
        }),
      reset: () => set({ accounts: [], groups: [DEFAULT_GROUP], activeId: null }),
    }),
    {
      name: 'bskyhq:accounts',
      version: 2,
      migrate: (persisted, version) => {
        const state = persisted as Partial<AccountsState> & {
          accounts?: Account[]
        }
        if (version < 2) {
          // v1 had no groups — fold every existing account into the default group.
          const accounts = state.accounts ?? []
          const def: AccountGroup = {
            ...DEFAULT_GROUP,
            accountIds: accounts.map((a) => a.id),
          }
          return { ...state, groups: [def] } as AccountsState
        }
        return state as AccountsState
      },
    },
  ),
)

export function activeAccount(): Account | null {
  const { accounts, activeId } = useAccounts.getState()
  return accounts.find((a) => a.id === activeId) ?? null
}
